import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Shield, Lock, Users, Eye, EyeOff, Database, Key, CheckCircle } from "lucide-react";

export default function Landing() {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await axios.post(`${API}${endpoint}`, payload);
      
      localStorage.setItem('remembral_token', response.data.token);
      localStorage.setItem('remembral_user', JSON.stringify(response.data.user));
      
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const openAuthDialog = (loginMode) => {
    setIsLogin(loginMode);
    setShowAuthDialog(true);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b border-slate-200 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="w-7 h-7 text-indigo-600" />
              <span className="text-xl font-bold text-slate-900">Remembral</span>
            </div>
            <div className="flex gap-3">
              <Button 
                variant="ghost" 
                onClick={() => openAuthDialog(true)}
                data-testid="nav-login-button"
              >
                Sign In
              </Button>
              <Button 
                className="bg-indigo-600 hover:bg-indigo-700"
                onClick={() => openAuthDialog(false)}
                data-testid="nav-signup-button"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </nav>



      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20 md:py-32">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-700 px-4 py-2 rounded-full text-sm font-medium mb-8">
            <Lock className="w-4 h-4" />
            Privacy-First Digital Vault
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
            Your Legacy. <br />
            <span className="text-indigo-600">Secured. Private. Yours.</span>
          </h1>
          
          <p className="text-lg sm:text-xl text-slate-600 mb-10 max-w-2xl mx-auto leading-relaxed">
            Secure your legacy. Protect what matters. Ensure your loved ones have access to critical information when they need it most.
          </p>


          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-indigo-600 hover:bg-indigo-700 text-lg px-8 py-6"
              onClick={() => openAuthDialog(false)}
              data-testid="hero-get-started-button"
            >
              Start Securing Your Legacy
            </Button>
          </div>
        </div>
      </section>

      {/* Privacy Promise Section */}
      <section className="bg-slate-50 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Built for Peace of Mind
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Simple, secure storage that ensures your family has what they need, when they need it.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200" data-testid="privacy-feature-encrypted">
                <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-5">
                  <Lock className="w-7 h-7 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">Secure Storage</h3>
                <p className="text-slate-600 leading-relaxed">
                  Your critical information is safely stored in a protected database, accessible only through your account credentials.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200" data-testid="privacy-feature-no-ads">
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-5">
                  <Shield className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">Privacy Focused</h3>
                <p className="text-slate-600 leading-relaxed">
                  No tracking. No ads. No selling your information. Your data is yours alone.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200" data-testid="privacy-feature-control">
                <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-5">
                  <Key className="w-7 h-7 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">You Control Access</h3>
                <p className="text-slate-600 leading-relaxed">
                  Only people you designate can access your vault. Add or remove access anytime. It's your data, your rules.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Simple. Secure. Essential.
              </h2>
              <p className="text-lg text-slate-600">
                Three steps to peace of mind for your family
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-12">
              <div className="text-center" data-testid="step-1">
                <div className="w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6">
                  1
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">Store Critical Info</h3>
                <p className="text-slate-600 leading-relaxed">
                  Add insurance policies, bank accounts, investments, and legal documents to your secure vault.
                </p>
              </div>

              <div className="text-center" data-testid="step-2">
                <div className="w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6">
                  2
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">Designate Dependents</h3>
                <p className="text-slate-600 leading-relaxed">
                  Choose trusted family members or individuals who should have access to your information.
                </p>
              </div>

              <div className="text-center" data-testid="step-3">
                <div className="w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6">
                  3
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">Rest Easy</h3>
                <p className="text-slate-600 leading-relaxed">
                  Your loved ones will have everything they need, exactly when they need it. No searching, no stress.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="bg-slate-50 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                What You Can Secure
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                { icon: Shield, title: "Life & Health Insurance", desc: "Policy numbers, coverage details, provider contacts" },
                { icon: Database, title: "Bank Accounts & Investments", desc: "Account numbers, brokerage details, portfolio information" },
                { icon: Users, title: "Emergency Contacts", desc: "Trusted advisors, lawyers, financial planners" },
                { icon: Lock, title: "Legal Documents", desc: "Will locations, trust information, power of attorney" }
              ].map((item, idx) => (
                <div key={idx} className="bg-white rounded-xl p-6 border border-slate-200 flex gap-4" data-testid={`use-case-${idx}`}>
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                    <p className="text-sm text-slate-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-3xl p-12 text-white">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Ready to Secure Your Legacy?
            </h2>
            <p className="text-lg text-indigo-100 mb-8">
              Start protecting what matters most. Free to get started.
            </p>
            <Button 
              size="lg"
              className="bg-white text-indigo-600 hover:bg-slate-100 text-lg px-8 py-6"
              onClick={() => openAuthDialog(false)}
              data-testid="cta-get-started-button"
            >
              Create Your Vault Now
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-slate-50 py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-indigo-600" />
              <span className="font-semibold text-slate-900">Remembral</span>
            </div>
            <p className="text-sm text-slate-600">
              Your data. Your control. Your peace of mind.
            </p>
          </div>
        </div>
      </footer>

      {/* Auth Dialog */}
      <Dialog open={showAuthDialog} onOpenChange={setShowAuthDialog}>
        <DialogContent data-testid="auth-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {isLogin ? 'Welcome Back' : 'Get Started'}
            </DialogTitle>
            <DialogDescription>
              {isLogin ? 'Sign in to access your vault' : 'Create your secure vault today'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="input-name"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
                required
                data-testid="input-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                required
                data-testid="input-password"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-indigo-600 hover:bg-indigo-700"
              disabled={loading}
              data-testid="auth-submit-button"
            >
              {loading ? 'Processing...' : (isLogin ? 'Sign In' : 'Create Account')}
            </Button>
          </form>

          <div className="mt-4 text-center text-sm">
            <span className="text-slate-600">
              {isLogin ? "Don't have an account?" : "Already have an account?"}
            </span>
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="ml-2 text-indigo-600 hover:text-indigo-700 font-medium"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? 'Sign Up' : 'Sign In'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}